import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Layers } from 'lucide-react';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export interface SummaryItem {
  key: string;
  name: string;
  icon: LucideIcon;
  connected: boolean;
  count?: number;
}

interface IntegrationsSummaryBarProps {
  items: SummaryItem[];
}

/**
 * Single-row "connected count / not connected" strip with rich hover tooltips on each badge.
 */
export const IntegrationsSummaryBar: React.FC<IntegrationsSummaryBarProps> = ({ items }) => {
  const connectedCount = items.filter((item) => item.connected).length;

  return (
    <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
      <div className="flex flex-wrap items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2 gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Layers className="h-3 w-3" />
          </span>
          <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
            Platform Connection Status
          </span>
        </div>
        <Tooltip content={`${connectedCount} of ${items.length} supported integrations are linked & active`} placement="top">
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 text-[9px] font-bold text-emerald-700 dark:text-emerald-300 cursor-pointer">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            {connectedCount} of {items.length} Connected
          </span>
        </Tooltip>
      </div>

      <div className="p-3 bg-white/70 dark:bg-slate-900/70">
        <div className="flex flex-wrap items-center gap-2">
          {items.map((item) => {
            const Icon = item.icon;
            const tooltipText = item.connected
              ? `${item.name}: Connected & Active${item.count ? ` (${item.count} account${item.count > 1 ? 's' : ''})` : ''}`
              : `${item.name}: Not connected — click to connect`;

            return (
              <Tooltip key={item.key} content={tooltipText} placement="top">
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-semibold shadow-2xs transition-all cursor-pointer',
                    item.connected
                      ? 'border-emerald-200/90 dark:border-emerald-800/60 bg-emerald-50/80 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100/70 dark:hover:bg-emerald-900/50'
                      : 'border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 text-slate-400 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                  )}
                >
                  <Icon className={cn('h-3.5 w-3.5', !item.connected && 'opacity-60')} />
                  <span>{item.name}</span>
                  <span
                    className={cn(
                      'h-1.5 w-1.5 rounded-full',
                      item.connected ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'
                    )}
                  />
                </span>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </Card>
  );
};

export default IntegrationsSummaryBar;
